/**
 * @architecture-header 1
 * @layer frontend-package
 * @domain contracts
 * @responsibility Define validated cross-runtime error, identity, and event contracts.
 * @state-owner none
 * @allowed-imports Zod
 * @forbidden React; DOM; application state
 * @public-api @vispo/contracts
 * @side-effects none
 * @tests contracts.test.ts
 */

import { z } from 'zod';

export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  requestId: z.string(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const roleSchema = z.enum(['teacher', 'student']);
export type Role = z.infer<typeof roleSchema>;

export const activeContextSchema = z.object({
  id: z.string().min(1),
  organizationId: z.string().min(1),
  classId: z.string().nullable(),
  role: roleSchema,
});

export type ActiveContext = z.infer<typeof activeContextSchema>;

export const accountRoleEntrySchema = z.object({
  role: z.string(),
  is_current: z.boolean(),
});

export const accountRolesResponseSchema = z.object({
  current_role: z.string(),
  roles: z.array(accountRoleEntrySchema),
});

export type AccountRolesResponse = z.infer<typeof accountRolesResponseSchema>;

export const examRowSchema = z
  .object({
    id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    total_points: z.number().nullable().optional(),
    pdf_storage_path: z.string().nullable().optional(),
    pdf_url: z.string().nullable().optional(),
    answer_pdf_storage_path: z.string().nullable().optional(),
    answer_pdf_url: z.string().nullable().optional(),
    answer_key: z.record(z.string(), z.unknown()).nullable().optional(),
    exam_layout: z.unknown().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .loose();

export type ExamRow = z.infer<typeof examRowSchema>;

export const uploadTargetSchema = z.object({
  uploadSessionId: z.string().min(1),
  bucket: z.string().min(1),
  storagePath: z.string().min(1),
  token: z.string().min(1),
  signedUploadUrl: z.url(),
  expiresIn: z.number().int().positive(),
  status: z.literal('pending'),
});

export type UploadTarget = z.infer<typeof uploadTargetSchema>;

export const uploadStatusSchema = z
  .object({
    uploadSessionId: z.string().min(1),
    domain: z.string(),
    bucket: z.string().min(1),
    storagePath: z.string().min(1),
    status: z.enum(['pending', 'ready', 'failed', 'expired']),
    fileName: z.string(),
    contentType: z.string(),
    failureReason: z.string().nullable().optional(),
    artifact: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .loose();

export type UploadStatus = z.infer<typeof uploadStatusSchema>;

export const pairedIngestSubmitSchema = z.object({
  job_id: z.string().min(1),
  status: z.string(),
});

export const pairedIngestResultSchema = z
  .object({
    exam_id: z.string().min(1),
    exam_class_file_id: z.string().min(1),
    question_count: z.number().int().nonnegative(),
    group_count: z.number().int().nonnegative(),
    review_required: z.boolean().optional().default(false),
    publish_state: z.string().optional().default('publishable'),
    review_reasons: z.array(z.string()).optional().default([]),
  })
  .loose();

export const pairedIngestStatusSchema = z
  .object({
    status: z.string(),
    result: pairedIngestResultSchema.nullable().optional(),
    error: z.string().nullable().optional(),
    warning: z.string().nullable().optional(),
    review_required: z.boolean().optional().default(false),
    publish_state: z.string().nullable().optional(),
    review_reasons: z.array(z.string()).optional().default([]),
    hints_total: z.number().int().nullable().optional(),
    hints_completed: z.number().int().nullable().optional(),
  })
  .loose();

export type PairedIngestStatus = z.infer<typeof pairedIngestStatusSchema>;

export const assignExamResponseSchema = z.object({
  assignments: z.array(
    z.object({
      class_id: z.string(),
      classwork_item_id: z.string(),
      attachment_id: z.string(),
      dispatched: z.number().int().nonnegative(),
    }),
  ),
  count: z.number().int().nonnegative(),
});

export const recallPreviewSchema = z
  .object({
    item_id: z.string(),
    active_count: z.number().int().nonnegative(),
    not_started_count: z.number().int().nonnegative(),
    in_progress_count: z.number().int().nonnegative(),
    submitted_count: z.number().int().nonnegative(),
    graded_count: z.number().int().nonnegative(),
    confirmation_level: z.enum(['none', 'standard', 'submitted']),
    requires_confirmation: z.boolean(),
  })
  .loose();

export type RecallPreview = z.infer<typeof recallPreviewSchema>;

export const recallResultSchema = z
  .object({
    message: z.string(),
    recalled: z.number().int().nonnegative().optional(),
  })
  .loose();

/**
 * The one seat vocabulary, declared at the wire boundary so the API, both hosts and the product
 * cannot drift apart. `SeatStatus` in workspace-web is derived from this, never re-declared.
 */
export const seatStatusSchema = z.enum(['offline', 'online', 'ok', 'stuck', 'need']);

export type SeatStatusValue = z.infer<typeof seatStatusSchema>;

export const interventionEventSchema = z.object({
  roomId: z.string(),
  studentId: z.string(),
  status: seatStatusSchema,
  questionIndex: z.number().int().nonnegative(),
  occurredAt: z.string(),
});

export type InterventionEvent = z.infer<typeof interventionEventSchema>;

export const reportWeakTopicSchema = z
  .object({
    name: z.string().min(1),
    accuracy: z.number().int().min(0).max(100).nullable().optional(),
    error_rate: z.number().nullable().optional(),
    correct: z.number().int().nonnegative().nullable().optional(),
    total: z.number().int().nonnegative().nullable().optional(),
  })
  .loose();

export const reportSummarySchema = z.object({
  student_name: z.string().min(1),
  period_start: z.string().min(1),
  period_end: z.string().min(1),
  study_hours: z.string(),
  accuracy_pct: z.number().int().min(0).max(100),
  exams_completed: z.number().int().nonnegative(),
  questions_answered: z.number().int().nonnegative(),
  weak_topics: z.array(reportWeakTopicSchema),
  ai_suggestions: z.array(z.string()),
});

export const generatedReportResponseSchema = z.object({
  report: reportSummarySchema,
  lang: z.string(),
  student_id: z.string().min(1),
  range: z.string(),
});

export type GeneratedReportResponse = z.infer<typeof generatedReportResponseSchema>;

export const weakTopicsResponseSchema = z.object({
  weak_topics: z.array(reportWeakTopicSchema),
  student_id: z.string().min(1),
  range: z.string(),
  total_topics_analyzed: z.number().int().nonnegative().nullable().optional(),
});

export const teacherStudentSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    grade: z.string().nullable().optional(),
    currentLevel: z.number(),
    xpPoints: z.number(),
    streakDays: z.number(),
    totalSessions: z.number().int().nonnegative(),
    correctAnswers: z.number().int().nonnegative(),
    totalQuestions: z.number().int().nonnegative(),
    lastSessionDate: z.string().nullable().optional(),
    progressTrend: z.array(
      z.object({
        date: z.string(),
        xp: z.number(),
        correctRate: z.number(),
        sessionsCompleted: z.number(),
      }),
    ),
  })
  .loose();

export const reportSendResponseSchema = z.object({
  success: z.boolean(),
  resend_id: z.string().nullable().optional(),
  recipient: z.email(),
  subject: z.string().min(1),
});

export const inboxItemSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(['submission', 'enrollment', 'overdue', 'graded', 'announcement']),
    title: z.string(),
    subtitle: z.string(),
    timestamp: z.string(),
    isRead: z.boolean(),
    isCompleted: z.boolean(),
    isUrgent: z.boolean(),
    meta: z
      .object({
        submissionId: z.string().nullable().optional(),
        classworkItemId: z.string().nullable().optional(),
        examId: z.string().nullable().optional(),
        classId: z.string().nullable().optional(),
        className: z.string().nullable().optional(),
        studentId: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .loose();

export type InboxItem = z.infer<typeof inboxItemSchema>;

export const classFileSchema = z
  .object({
    id: z.string().min(1),
    class_id: z.string().min(1),
    teacher_id: z.string().min(1),
    file_type: z.enum(['document', 'exam']),
    name: z.string().min(1),
    mime_type: z.string().nullable(),
    size_bytes: z.number().int().nonnegative().nullable(),
    storage_path: z.string().nullable(),
    signed_url: z.url().nullable(),
    exam_id: z.string().nullable(),
    folder_path: z.string(),
    status: z.enum(['ready', 'uploading', 'parsing', 'error', 'deleted']),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .loose();

export type ClassFile = z.infer<typeof classFileSchema>;

export const reportDraftSchema = z
  .object({
    id: z.string().min(1),
    content: z.string(),
    status: z.string(),
    kind: z.string(),
    student_id: z.string(),
  })
  .loose();

export const reportDraftEnvelopeSchema = z.object({
  draft: reportDraftSchema,
});
